import { Navigation, MYOBLogo, CaretIcon, SignOutIcon, UserProfileIcon } from "@myob/myob-widgets";
import { useContext } from "react";
import { useMsal } from "@azure/msal-react";
import { Link } from "react-router-dom";
import UserContext from "../../auth/UserContext";
import PurchasedLeaveWindowContext from "../../auth/PurchasedLeaveWindowContext";
import UserFactory from "../../factories/UserFactory";
import { logoHomeStyle, customHamburgerStyle } from "./Header.css.ts";

const formTypes = [
  {
    id: "HelpMeFormsDeparture",
    name: "departure",
    enabled: {
      create: true,
      list: true,
    },
    directLink: false,
    title: "Departure Form",
  },
  {
    id: "HelpMeFormsNameChange",
    name: "nameChange",
    enabled: {
      create: true,
      list: false,
    },
    directLink: true,
    url: "https://helpme.myob.com/hc/en-us/requests/new?ticket_form_id=12237031585049&tf_12236885984921=people_form_name_change",
    title: "Name Change Form",
  },
  {
    id: "HelpMeFormsTransfer",
    name: "role-update",
    enabled: {
      create: true,
      list: true,
    },
    directLink: false,
    title: "Role Update/Transfer Form",
  },
  {
    id: "BulkReportingLineChange",
    name: "bulk-reporting-line-change",
    enabled: {
      create: true,
      list: false,
    },
    directLink: true,
    url: "https://helpme.myob.com/hc/en-us/requests/new?ticket_form_id=11439419669401&tf_11438992067993=pl_employment_bulk_transfer",
    title: "Bulk Reporting Line Change",
  },
  {
    id: "ThirdPartyContractor",
    name: "third-party-contractor",
    enabled: {
      create: true,
      list: true,
    },
    directLink: false,
    title: "Third Party Contractor Onboarding Form",
  },
  {
    id: "HelpMeFormsNewStarter",
    name: "newStarter",
    enabled: {
      create: false,
      list: false,
    },
    directLink: true,
    title: "New Starter Form",
  },
  {
    id: "EmploymentFormsOnboarding",
    name: "onboarding",
    enabled: {
      create: false,
      list: true,
    },
    directLink: true,
    title: "Onboarding Form",
  },
  {
    id: "EmployeeFormsParentalLeave",
    name: "parental-leave",
    enabled: {
      create: true,
      list: false,
    },
    directLink: false,
    title: "Parental Leave Form",
  },
  {
    id: "EmployeeFormsProofOfEmployment",
    name: "proof-of-employment",
    enabled: {
      create: true,
      list: false,
    },
    directLink: false,
    title: "Proof of Employment Form",
  },
  {
    id: "EmployeeFormsEoyInitialRating",
    name: "eoy-rating",
    enabled: {
      create: true,
      list: false,
      peopleLeaderOnly: true,
    },
    directLink: false,
    title: "End of Year Indicative Rating for Pre-Calibration process",
  },
  {
    id: "PurchasedLeave",
    name: "purchased-leave",
    enabled: {
      create: true,
      list: true,
      pxOnly: true,
    },
    directLink: false,
    title: "Purchased Leave",
  },
];

const PEP = "PEOPLE EXPERIENCE PLATFORMS";
const TEAM_MEMBER = "TEAM MEMBER";

export default function Header() {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();

  const user = useContext(UserContext);
  const { windowOpen: purchasedLeaveWindowOpen, pxWindowOpen: purchasedLeavePxWindowOpen } =
    useContext(PurchasedLeaveWindowContext);

  const brand = (
    <Navigation.Brand width="73px">
      <Link to="/" className={logoHomeStyle}>
        <MYOBLogo />
      </Link>
    </Navigation.Brand>
  );

  const isPeopleLeader = () => {
    if (user?.teamMemberLevel?.toUpperCase() !== TEAM_MEMBER) {
      return true;
    }

    return false;
  };

  const checkVisibility = ({ enabled }) => {
    if (user?.team?.toUpperCase() === PEP) {
      return true;
    }

    // menu option is visible if enabled.peopleLeaderOnly is undefined
    if (enabled.peopleLeaderOnly === undefined) {
      return true;
    }

    // menu option is visible if enabled.peopleLeaderOnly is true and user is a people leader
    if (enabled.peopleLeaderOnly && isPeopleLeader()) {
      return true;
    }

    return false;
  };

  // Purchased Leave's "New Form" link only opens while the annual application window is open; People
  // Experience also see it during their wider access window (the backend only ever returns
  // pxWindowOpen: true for full-access users). The dashboard link below (canSeeDashboard) is unaffected by the window.
  const canCreatePurchasedLeaveNow = (formType) => {
    if (formType.id === "PurchasedLeave") {
      return purchasedLeaveWindowOpen || purchasedLeavePxWindowOpen;
    }

    return true;
  };

  const canSeeDashboard = (formType) => {
    if (!formType.name || !formType.enabled.list) {
      return false;
    }
    if (formType.enabled.pxOnly) {
      return UserFactory.create(user || {}).hasFullAccess();
    }
    return true;
  };

  const getDashboardTitle = (formTitle) => {
    if (formTitle === "Third Party Contractor Onboarding Form") {
      return `${formTitle} - Submissions`;
    }

    return `${formTitle} - Status Dashboard`;
  };

  const primary = [
    <Navigation.Menu
      key="menu-new-form"
      label="New Form"
      icon={<CaretIcon />}
      items={formTypes
        .filter(
          (formType) =>
            formType.name &&
            formType.enabled.create &&
            checkVisibility(formType) &&
            canCreatePurchasedLeaveNow(formType),
        )
        .map((formType) => (
          <Link key={`menu-new-${formType.id}`} to={formType.url || `/form/${formType.name}`} className={logoHomeStyle}>
            <Navigation.MenuLink label={formType.title} />
          </Link>
        ))}
    />,
    <Navigation.Menu
      key="menu-manage"
      label="Employment Forms Dashboards"
      icon={<CaretIcon />}
      items={formTypes
        .filter((formType) => canSeeDashboard(formType))
        .map((formType) => (
          <Link key={`menu-list-${formType.id}`} to={`/form/${formType.name}/list`} className={logoHomeStyle}>
            <Navigation.MenuLink label={getDashboardTitle(formType.title)} />
          </Link>
        ))}
    />,
    // <Navigation.Menu
    //   key="menu-report"
    //   label="Reports"
    //   icon={<CaretIcon />}
    //   items={[
    //     <Link key={`menu-list-contractor-expiry`} to={`/reports/contractor-expiry`} className={logoHomeStyle}>
    //       <Navigation.MenuLink label={"3rd Party Contractor Expiry"} />
    //     </Link>,
    //   ]}
    // />,
  ];

  const secondary = [
    <Navigation.Menu
      key="user-menu"
      label={`Hello ${user ? `${user.firstName} ${user.surname}` : activeAccount?.name || ""}`}
      icon={<UserProfileIcon />}
      items={[
        <Link key={`logout`} to={`/logout`} className={logoHomeStyle}>
          <Navigation.MenuLink label={"Logout"} icon={<SignOutIcon />} iconRight />
        </Link>,
      ]}
    />,
  ];

  return <Navigation className={customHamburgerStyle} brand={brand} primary={primary} settings={secondary} />;
}
